/**
 * Multi-pane chart: price on top, one native sub-pane per non-overlay indicator.
 *
 * lightweight-charts v5 ships a real multi-pane API: one chart instance,
 * `chart.addSeries(def, opts, paneIndex)` creates (or reuses) a sub-pane,
 * `chart.panes()` enumerates them, and every pane shares the chart's single
 * time scale and single crosshair by construction. No cross-chart sync, no
 * ricochet guards, no drift between pane time axes — the alignment is a
 * property of the data model, not of a hand-tuned subscription.
 *
 * The library draws no pane header, so title / legend / remove-button lives
 * as absolutely positioned HTML inside the chart container, with its y-offset
 * computed from `IPaneApi.getHTMLElement()`'s rect.
 */
import {
  type CandlestickData,
  CandlestickSeries,
  ColorType,
  type HistogramData,
  HistogramSeries,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type LineData,
  type WhitespaceData,
  LineSeries,
  LineStyle,
  type SeriesMarker,
  type Time,
  createChart,
  createSeriesMarkers,
} from "lightweight-charts";
import { type Bar, formatVolume, type PineLabel, type PineTable } from "@tt/shared";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { IndicatorLegend, type LegendControls } from "./IndicatorLegend";
import { ChartFooter } from "./ChartFooter";

export interface SeriesSpec {
  /** Unique across the chart. Two EMAs share a display name, so the name
   *  alone cannot key a list. */
  key: string;
  name: string;
  values: (number | null)[];
  color: string;
  kind: string;
  linewidth: number;
  /** Per-bar colour override, used by Pine scripts that colour a plot. */
  colors?: (string | null)[] | null;
  /** Own price scale, for a series whose units are not price. An RSI moved
   *  onto the price pane spans 0-100 and would otherwise compress the candles
   *  into a flat line at the bottom of the chart. */
  scaleId?: string;
}

export interface PaneSpec {
  id: string;
  title: string;
  plots: SeriesSpec[];
  levels?: { value: number; color: string; style: string; label?: string }[];
  scale?: number[] | null;
  fills?: { plot_a: string; plot_b: string; color: string }[];
  controls?: LegendControls;
}

/** Series drawn on the price pane, grouped so the legend can label and
 *  control them per indicator rather than per line. */
export interface OverlayGroup {
  id: string;
  title: string;
  series: SeriesSpec[];
  controls?: LegendControls;
}

/**
 * A bar's time in New York, which is the only clock these bars mean anything
 * in. `tick` is the axis, which is terse by necessity; the crosshair readout
 * can afford the date as well.
 */
function formatET(epochSeconds: number, tfSeconds: number, tick: boolean): string {
  const d = new Date(epochSeconds * 1000);
  const intraday = tfSeconds < 86400;
  if (!intraday) {
    return d.toLocaleDateString("en-US", {
      timeZone: ET,
      month: "short",
      day: "numeric",
      // The year only where it changes something: on a daily chart spanning
      // years every label would otherwise carry one.
      ...(tick ? {} : { year: "numeric" }),
    });
  }
  const time = d.toLocaleTimeString("en-US", {
    timeZone: ET,
    hour: "2-digit",
    minute: "2-digit",
    ...(tfSeconds < 60 ? { second: "2-digit" } : {}),
    hour12: false,
  });
  if (tick) return time;
  const day = d.toLocaleDateString("en-US", { timeZone: ET, month: "short", day: "numeric" });
  return `${day} ${time}`;
}

const ET = "America/New_York";

export interface ChartProps {
  bars: Bar[];
  /** Drives the time axis: sub-minute bars need seconds on the labels, and
   *  daily and above need no clock time at all. */
  timeframe?: string;
  /** Shown at the left of the footer strip, e.g. "AAPL · 5m". */
  caption?: string;
  /** Identifies the dataset. The view is fitted when this changes and left
   *  alone when it does not, which is what separates "a different chart" from
   *  "the same chart with a newer bar". */
  viewKey?: string;
  /** Series drawn on the price pane, grouped by indicator. */
  overlayGroups?: OverlayGroup[];
  /** Independent panes stacked beneath the price pane. */
  panes?: PaneSpec[];
  /** Shape plots from a Pine script, drawn as markers on the candles. */
  shapes?: SeriesSpec[];
  /** label.new() calls, drawn as markers above or below their bar. */
  labels?: PineLabel[];
  /** table.new() panels, pinned to a corner of the price pane. */
  tables?: PineTable[];
  priceLines?: { price: number | null; title: string; color: string; style: string }[];
  /** off hides it, overlay pins it to the base of the price pane, pane gives
   *  it its own row beneath. */
  volumeMode?: "off" | "overlay" | "pane";
  trades?: { entry_bar: number; exit_bar: number | null; direction: string; pnl: number }[];
  onRemovePane?: (id: string) => void;
  /** Rendered over the price pane, with the chart handles needed to project
   *  chart coordinates to pixels. */
  /** Following today's session; the footer shows a Live button when set. */
  live?: boolean;
  onToggleLive?: () => void;
  renderPriceOverlay?: (ctx: {
    chart: IChartApi | null;
    series: ISeriesApi<"Candlestick"> | null;
  }) => React.ReactNode;
  /** A strip beneath the price pane sharing its time axis, for event markers. */
  renderEventStrip?: (ctx: { chart: IChartApi | null }) => React.ReactNode;
  /** Fired as the mouse moves over the chart, carrying the hovered bar's
   *  epoch seconds (or null when the cursor leaves). The multi-chart
   *  workspace uses this to broadcast crosshair time to its siblings so a
   *  hover on one tile highlights the same moment on every other tile. */
  onCrosshair?: (time: number | null) => void;
  /** Position the crosshair here, driven externally. The chart does not fire
   *  `onCrosshair` for synced moves -- otherwise the sync would echo across
   *  every tile in a feedback loop. */
  syncCrosshairTime?: number | null;
}

// Module-level constants: a `= []` default allocates a fresh array on every
// render, so every effect depending on it would re-run every render.
const NO_SERIES: SeriesSpec[] = [];
const NO_LABELS: PineLabel[] = [];
const NO_TABLES: PineTable[] = [];
const NO_GROUPS: OverlayGroup[] = [];
const NO_PANES: PaneSpec[] = [];
const NO_LINES: NonNullable<ChartProps["priceLines"]> = [];
const NO_TRADES: NonNullable<ChartProps["trades"]> = [];

const PANE_HEIGHT = 118;
const PANE_MIN = 60;
const PANE_MAX = 600;
const PANE_HEIGHTS_KEY = "tt.chart.paneHeights";

function loadPaneHeights(): Record<string, number> {
  try {
    const raw = localStorage.getItem(PANE_HEIGHTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function savePaneHeights(h: Record<string, number>): void {
  try { localStorage.setItem(PANE_HEIGHTS_KEY, JSON.stringify(h)); } catch {
    // private mode / quota — resize still works for this session.
  }
}

/**
 * Chart colours, mirroring the CSS custom properties in styles.css.
 *
 * lightweight-charts paints to canvas and takes literal colours, so it cannot
 * read the CSS variables. Keeping them in one named table at least makes the
 * duplication visible: change a token there, change it here.
 */
type ChartColors = {
  bg: string;
  grid: string;
  border: string;
  crosshair: string;
  crosshairLabel: string;
  up: string;
  down: string;
  priceLine: string;
  text: string;
};

/** Dark palette (default). Mirrored in :root in styles.css. */
const C_DARK: ChartColors = {
  bg: "#090c12",
  grid: "#151a24",
  border: "#1e2431",
  crosshair: "#4a566e",
  crosshairLabel: "#222a3c",
  up: "#22c55e",
  down: "#f04452",
  priceLine: "#3a4459",
  text: "#9aa5b8",
} as const;

/** Light palette. Mirrored in :root[data-theme="light"] in styles.css. */
const C_LIGHT: ChartColors = {
  bg: "#ffffff",
  grid: "#eef1f6",
  border: "#e0e3eb",
  crosshair: "#9aa3b4",
  crosshairLabel: "#eef1f6",
  up: "#089981",
  down: "#f23645",
  priceLine: "#8a93a6",
  text: "#5d6475",
} as const;

/** Current theme's colour table. Read fresh so a theme flip picks up the
 *  new palette without a page reload -- the chart listens for a mutation on
 *  <html>'s data-theme attribute and re-applies baseOptions. */
function colors(): ChartColors {
  if (typeof document === "undefined") return C_DARK;
  return document.documentElement.getAttribute("data-theme") === "light" ? C_LIGHT : C_DARK;
}

// Legacy export name: a lot of this file reads C.up / C.bg directly. Keeping
// the identifier means we can swap the backing table without hunting every
// call site. getters re-read on each access, so the chart's series options
// (built fresh on every data refresh) pick up a theme change automatically.
const C = new Proxy({} as ChartColors, {
  get(_t, key: string) { return (colors() as unknown as Record<string, string>)[key]; },
}) as unknown as ChartColors;

/** Only the boundaries the axis cares about: under a minute (show seconds),
 *  under a day (show clock time), and daily or longer (dates only). */
const TF_SECONDS: Record<string, number> = {
  "1s": 1, "5s": 5, "10s": 10, "15s": 15, "30s": 30,
  "1m": 60, "2m": 120, "3m": 180, "5m": 300, "10m": 600, "15m": 900,
  "30m": 1800, "45m": 2700, "1h": 3600, "2h": 7200, "3h": 10800, "4h": 14400,
  "1d": 86400, "1w": 604800,
};

/** Chart-wide defaults. A function so a theme flip is picked up on the
 *  next `applyOptions` call -- the Proxy on `C` reads fresh each access. */
function buildBaseOptions() {
  const c = colors();
  return {
    layout: {
      background: { type: ColorType.Solid, color: c.bg },
      textColor: c.text,
      fontSize: 12.5,
      fontFamily:
        '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
      // Lightweight Charts is Apache-2.0 and its docs permit turning this off
      // once the attribution is met elsewhere: the copyright line from its
      // NOTICE plus a visible link to tradingview.com. Both are in the Data
      // sources panel, which is where every other provider is credited.
      attributionLogo: false,
    },
    grid: {
      vertLines: { color: c.grid },
      horzLines: { color: c.grid },
    },
    crosshair: {
      mode: 0 as const,
      vertLine: {
        color: c.crosshair, width: 1 as const, style: LineStyle.Dashed,
        labelBackgroundColor: c.crosshairLabel,
      },
      horzLine: {
        color: c.crosshair, width: 1 as const, style: LineStyle.Dashed,
        labelBackgroundColor: c.crosshairLabel,
      },
    },
    rightPriceScale: {
      borderColor: c.border,
      scaleMargins: { top: 0.08, bottom: 0.08 },
      // Fixed-width price scale so the plotting area starts at the same x
      // pixel in every pane. Without this, an oscillator whose labels are
      // wider than the price pane's (e.g. "-100.00" vs "334.5") takes a
      // wider column and the same bar index renders at a slightly
      // different x position in each pane; the dashed crosshair line then
      // looks misaligned even when logical-range sync is exact.
      minimumWidth: 72,
    },
    timeScale: {
      borderColor: c.border, rightOffset: 4, barSpacing: 7,
      // Without timeVisible an intraday axis shows dates only, so every
      // bar inside a session carries the same label and the axis stops
      // locating anything. Set per-timeframe in the effect below.
      timeVisible: true, secondsVisible: false,
    },
  };
}
const baseOptions = buildBaseOptions();

function lineWidthOf(n: number): 1 | 2 | 3 | 4 {
  return Math.max(1, Math.min(4, Math.round(n))) as 1 | 2 | 3 | 4;
}

function toLineData(values: (number | null)[], bars: Bar[]): (LineData | WhitespaceData)[] {
  // Emit a `{time}`-only whitespace entry on bars where the indicator has
  // no value (warmup period, missing data) instead of dropping them. The
  // series then spans every bar's time, so an oscillator pane's time axis
  // matches the price pane's exactly even when the first N bars are NaN
  // -- without this, a 14-period Williams %R's pane rendered only the
  // last (N − 14) bars' worth of x-axis, and cross-pane sync landed the
  // dashed crosshair on different dates in each pane. See the time-range
  // sync effect above.
  const out: (LineData | WhitespaceData)[] = [];
  const n = Math.min(values.length, bars.length);
  for (let i = 0; i < n; i++) {
    const v = values[i];
    const bar = bars[i];
    if (!bar) continue;
    if (v === null || v === undefined || !Number.isFinite(v)) {
      out.push({ time: bar.time as Time });
      continue;
    }
    out.push({ time: bar.time as Time, value: v });
  }
  return out;
}

export function Chart({
  bars,
  timeframe = "1d",
  caption,
  viewKey = "",
  overlayGroups = NO_GROUPS,
  panes = NO_PANES,
  shapes = NO_SERIES,
  labels = NO_LABELS,
  tables = NO_TABLES,
  priceLines = NO_LINES,
  volumeMode = "overlay",
  trades = NO_TRADES,
  onRemovePane,
  live,
  onToggleLive,
  renderPriceOverlay,
  renderEventStrip,
  onCrosshair,
  syncCrosshairTime,
}: ChartProps) {
  const priceRef = useRef<HTMLDivElement>(null);
  const priceChart = useRef<IChartApi | null>(null);
  const candles = useRef<ISeriesApi<"Candlestick"> | null>(null);
  // v5 moved markers out of the series and into their own plugin. It is
  // created once with the series and kept, because createSeriesMarkers
  // attaches a primitive -- calling it per update would stack a new one on
  // every render and leave the old markers drawn underneath.
  const markersApi = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  /** The dataset last drawn, and how many bars it had; see the bars effect. */
  const lastView = useRef("");
  const lastCount = useRef(0);
  const lastBars = useRef<CandlestickData[]>([]);
  const volume = useRef<ISeriesApi<"Histogram"> | null>(null);
  const overlaySeries = useRef<ISeriesApi<"Line" | "Histogram">[]>([]);
  const overlayLines = useRef<IPriceLine[]>([]);

  // Native sub-pane series, keyed by pane id. The native pane is created by
  // `priceChart.addSeries(def, opts, paneIndex)` and removed either implicitly
  // (when all its series are gone and preserveEmptyPane is off) or explicitly
  // via `priceChart.removePane(index)`.
  const paneSeries = useRef<Map<string, ISeriesApi<"Line" | "Histogram">[]>>(new Map());
  // Per-pane price lines (horizontal levels) added via createPriceLine, so
  // they can be removed cleanly on each rebuild without leaking.
  const panePriceLines = useRef<Map<string, IPriceLine[]>>(new Map());
  const [strategyTrades, setStrategyTrades] = useState<Array<{
    number: number; direction: string;
    entry_ts: string | null; exit_ts: string | null;
    entry_price: number; exit_price: number | null;
    pnl: number;
  }>>([]);
  const [hover, setHover] = useState<{ bar: Bar | null; index: number | null }>({
    bar: null, index: null,
  });
  // Bumped once the price chart exists, so an overlay re-renders with real
  // handles rather than nulls captured on the first paint.
  const [ready, setReady] = useState(0);

  // Per-pane height, so a user can drag the gap between an oscillator and
  // the price chart to grow or shrink the oscillator. Persisted to
  // localStorage keyed by pane id, so "RSI is 180px tall" carries across
  // sessions and across symbols. On a native pane we call
  // IPaneApi.setHeight; the main pane absorbs whatever vertical space the
  // sub-panes leave behind.
  const [paneHeights, setPaneHeights] = useState<Record<string, number>>(loadPaneHeights);
  const paneHeightOf = (id: string): number => {
    const h = paneHeights[id];
    return Math.max(PANE_MIN, Math.min(PANE_MAX, typeof h === "number" ? h : PANE_HEIGHT));
  };
  // Latest paneHeightOf, captured in a ref so an effect run from a stale
  // closure still reads the current heights.
  const paneHeightOfRef = useRef(paneHeightOf);
  paneHeightOfRef.current = paneHeightOf;

  // Measured { top, height } of every native pane, read from each pane's
  // getHTMLElement().getBoundingClientRect(). Drives the absolutely-positioned
  // HTML overlays (title, legend, remove button, drag handle) that the
  // library does not draw. Recomputed on every pane add/remove, every
  // ResizeObserver tick, and every paneHeights change.
  const [paneLayout, setPaneLayout] = useState<Array<{ top: number; height: number }>>([]);
  // Monotonic counter -- bumping it schedules a layout recompute in a
  // dedicated effect so the measurement happens AFTER the library has
  // finished the current frame's reflow.
  const [layoutTick, setLayoutTick] = useState(0);
  const bumpLayout = () => setLayoutTick((n) => n + 1);

  const paneIds = useMemo(() => panes.map((p) => p.id).join("|"), [panes]);
  const overlays = useMemo(
    () => overlayGroups.flatMap((g) => g.series),
    [overlayGroups],
  );

  // Re-apply chart options when the global theme flips. One MutationObserver
  // on <html>'s data-theme attribute does both the main price chart and any
  // overlay charts, since each addSeries reads a fresh colour from the C
  // Proxy. The chart library's own canvas doesn't react to CSS; this is the
  // only path for a theme change to reach it without reloading.
  useEffect(() => {
    const target = document.documentElement;
    const sync = () => {
      priceChart.current?.applyOptions(buildBaseOptions());
      candles.current?.applyOptions({
        upColor: C.up, downColor: C.down,
        borderUpColor: C.up, borderDownColor: C.down,
        wickUpColor: C.up, wickDownColor: C.down,
        priceLineColor: C.priceLine,
      });
    };
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "attributes" && m.attributeName === "data-theme") { sync(); break; }
      }
    });
    mo.observe(target, { attributes: true, attributeFilter: ["data-theme"] });
    return () => mo.disconnect();
  }, []);

  // ── price chart, created once ────────────────────────────────────────
  useEffect(() => {
    if (!priceRef.current) return;
    const pc = createChart(priceRef.current, {
      ...buildBaseOptions(),
      width: priceRef.current.clientWidth,
      height: priceRef.current.clientHeight,
    });
    priceChart.current = pc;
    // A handle for the console in development: the time scale is the thing
    // that gets reset, and there is no other way to watch it from outside.
    if (import.meta.env.DEV) {
      (window as unknown as { __chart?: IChartApi }).__chart = pc;
    }
    candles.current = pc.addSeries(CandlestickSeries, {
      upColor: C.up, downColor: C.down,
      borderUpColor: C.up, borderDownColor: C.down,
      wickUpColor: C.up, wickDownColor: C.down,
      priceLineColor: C.priceLine,
    });
    markersApi.current = createSeriesMarkers(candles.current, []);
    volume.current = pc.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "vol",
      priceLineVisible: false,
      lastValueVisible: false,
    });
    // Pin volume to the bottom fifth so it never competes with price.
    pc.priceScale("vol").applyOptions({
      scaleMargins: { top: 0.78, bottom: 0 },
      visible: false,
    });

    // ResizeObserver coalesced to one apply per animation frame -- a window
    // drag or pane-handle drag fires the callback dozens of times per
    // second; without the rAF guard each one triggers a full chart
    // re-layout and the whole thing feels sticky.
    let resizeRaf: number | null = null;
    const ro = new ResizeObserver(() => {
      if (resizeRaf !== null) return;
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = null;
        if (priceRef.current) {
          pc.applyOptions({
            width: priceRef.current.clientWidth,
            height: priceRef.current.clientHeight,
          });
          bumpLayout();
        }
      });
    });
    ro.observe(priceRef.current);
    setReady((v) => v + 1);
    return () => {
      ro.disconnect();
      pc.remove();
      priceChart.current = null;
      candles.current = null;
      volume.current = null;
      markersApi.current = null;
      overlaySeries.current = [];
      overlayLines.current = [];
    };
  }, []);

  /** 20-bar average volume, for relative-volume shading.
   *
   *  Presentation only — it decides how bright a bar is drawn, nothing more.
   *  Any volume figure a decision rests on comes from the indicator engine,
   *  which is where the one validated implementation lives.
   */
  const relativeVolume = useMemo(() => {
    const out: number[] = new Array(bars.length).fill(NaN);
    const window = 20;
    let sum = 0;
    for (let i = 0; i < bars.length; i++) {
      sum += bars[i]!.volume;
      if (i >= window) sum -= bars[i - window]!.volume;
      const n = Math.min(i + 1, window);
      const avg = sum / n;
      out[i] = avg > 0 ? bars[i]!.volume / avg : NaN;
    }
    return out;
  }, [bars]);

  const volumeBars = useMemo<HistogramData[]>(
    () =>
      bars.map((b, i) => {
        const up = b.close >= b.open;
        const rel = relativeVolume[i] ?? 1;
        // Opacity carries the magnitude, so an unusually heavy bar stands out
        // at a glance instead of needing the number read off the axis.
        // Lowered across the board now that the bars sit under the price
        // rather than behind it: at the old weights the band competed with
        // the candles for attention even when it was no longer overlapping
        // them. The spread between levels is kept, since that is what makes
        // an unusually heavy bar legible without reading the axis.
        const alpha =
          rel >= 2.5 ? "aa" : rel >= 1.5 ? "7a" : rel >= 0.8 ? "4d" : "2b";
        return {
          time: b.time as Time,
          value: b.volume,
          color: `${up ? C.up : C.down}${alpha}`,
        };
      }),
    [bars, relativeVolume],
  );

  // ── price data ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!candles.current || !volume.current) return;
    const cd: CandlestickData[] = bars.map((b) => ({
      time: b.time as Time, open: b.open, high: b.high, low: b.low, close: b.close,
    }));
    const ts = priceChart.current?.timeScale();
    // What the reader is looking at, captured before the data is replaced.
    // setData resets the time scale, so without this a refresh threw the
    // zoom away -- which with live mode polling every fifteen seconds meant
    // the chart snapped back four times a minute while being read.
    const wasAtRightEdge = (() => {
      const lr = ts?.getVisibleLogicalRange();
      return lr != null && lastCount.current > 0 && lr.to >= lastCount.current - 2;
    })();
    // Anchored to a bar, not to a time span. getVisibleRange returns clock
    // time, and an intraday window crosses session gaps -- restoring it
    // mapped thirty bars onto eight and the chart zoomed in on every
    // refresh. The bar at the left edge, found again by its own timestamp,
    // survives the window rolling forward.
    const prevLogical = ts?.getVisibleLogicalRange() ?? null;
    const prev = lastBars.current;
    const anchorTime =
      prevLogical && prev.length > 0
        ? prev[Math.min(prev.length - 1, Math.max(0, Math.round(prevLogical.from)))]?.time
        : undefined;
    const span = prevLogical ? prevLogical.to - prevLogical.from : 0;
    const sameView = viewKey !== "" && viewKey === lastView.current;

    candles.current.setData(cd);
    volume.current.setData(volumeMode === "overlay" ? volumeBars : []);
    // Price keeps clear of the volume band rather than being drawn through
    // it. Volume is pinned to the bottom 22% of this same pane, and with
    // price free to use the bottom 8% the two drew over each other -- a
    // green bar behind a green candle in the same hue, which is why they
    // looked blended rather than layered. Giving price a floor above the
    // band is what every terminal does with overlaid volume.
    priceChart.current?.priceScale("right").applyOptions({
      scaleMargins: { top: 0.08, bottom: volumeMode === "overlay" ? 0.24 : 0.08 },
    });
    if (!sameView) {
      // A different symbol or timeframe: nothing about the old view applies.
      ts?.fitContent();
    } else if (live || wasAtRightEdge) {
      // Follow the newest bar.
      //
      // Unconditional while live, because the request returns a fixed window
      // of the most recent bars: when one forms, the oldest drops off and
      // every index shifts by one. Holding a position in that window walks
      // backwards through time at exactly the rate new bars arrive, which is
      // what "it goes backward" was. Zoom is kept -- scrollToRealTime moves
      // the window without touching bar spacing -- so this follows at
      // whatever magnification was chosen.
      //
      // Browsing history is what turning live off is for.
      ts?.scrollToRealTime();
    } else if (anchorTime !== undefined && span > 0) {
      const idx = cd.findIndex((b) => b.time === anchorTime);
      // Gone from the window entirely: nothing sensible to restore to, so
      // the view is left as the new data put it.
      if (idx >= 0) ts?.setVisibleLogicalRange({ from: idx, to: idx + span });
    }
    lastView.current = viewKey;
    lastCount.current = cd.length;
    lastBars.current = cd;
  }, [bars, volumeBars, volumeMode, viewKey, live]);

  // ── overlays on the price pane ───────────────────────────────────────
  useEffect(() => {
    const pc = priceChart.current;
    if (!pc) return;
    for (const s of overlaySeries.current) pc.removeSeries(s as ISeriesApi<"Line">);
    overlaySeries.current = [];

    for (const spec of overlays) {
      const scaleId = spec.scaleId ?? "right";
      if (spec.kind === "histogram" || spec.kind === "columns") {
        const s = pc.addSeries(HistogramSeries, {
          color: spec.color, priceLineVisible: false, lastValueVisible: false,
          priceScaleId: scaleId,
        });
        s.setData(toLineData(spec.values, bars).map((d) => ({ ...d, color: spec.color })));
        overlaySeries.current.push(s);
      } else {
        const s = pc.addSeries(LineSeries, {
          color: spec.color,
          lineWidth: lineWidthOf(spec.linewidth),
          priceLineVisible: false,
          lastValueVisible: spec.scaleId === undefined,
          crosshairMarkerVisible: true,
          priceScaleId: scaleId,
          // `cross` is used for Parabolic SAR, which is dots rather than a line.
          lineStyle: spec.kind === "cross" ? LineStyle.Dotted : LineStyle.Solid,
        });
        // Per-bar colours again: this is the path a Pine overlay takes, and
        // it is where a two-coloured supertrend actually lands.
        const over = spec.colors;
        s.setData(
          over
            ? toLineData(spec.values, bars).map((d, i) => ({
                ...d,
                color: over[i] ?? spec.color,
              }))
            : toLineData(spec.values, bars),
        );
        overlaySeries.current.push(s);
      }
      if (spec.scaleId) {
        // Hidden and confined to the upper half, so it reads as an inset rather
        // than competing with price for the axis.
        pc.priceScale(spec.scaleId).applyOptions({
          visible: false,
          scaleMargins: { top: 0.05, bottom: 0.55 },
        });
      }
    }
  }, [overlays, bars]);

  // ── markers: Pine shape plots and backtest trades ────────────────────
  useEffect(() => {
    if (!candles.current) return;
    const markers: SeriesMarker<Time>[] = [];
    for (const spec of shapes) {
      for (let i = 0; i < spec.values.length && i < bars.length; i++) {
        const v = spec.values[i];
        const bar = bars[i];
        if (v === null || v === undefined || !bar) continue;
        const above = v >= bar.close;
        markers.push({
          time: bar.time as Time,
          position: above ? "aboveBar" : "belowBar",
          color: spec.colors?.[i] ?? spec.color,
          shape: above ? "arrowDown" : "arrowUp",
          text: spec.name.length < 14 ? spec.name : undefined,
        });
      }
    }
    // "Trades on chart", "Signal labels" and "Quantity" from the Style tab
    // of the Strategy Settings dialog. Read on each render -- the state
    // lives in localStorage so other tabs' changes are reflected.
    const styleShowTrades = (() => {
      try { return localStorage.getItem("tt.bt.style.trades") !== "false"; }
      catch { return true; }
    })();
    const styleShowSignals = (() => {
      try { return localStorage.getItem("tt.bt.style.signals") !== "false"; }
      catch { return true; }
    })();
    const styleShowQty = (() => {
      try { return localStorage.getItem("tt.bt.style.qty") !== "false"; }
      catch { return true; }
    })();

    if (styleShowTrades) {
      for (const t of trades) {
        const eb = bars[t.entry_bar];
        if (eb) {
          markers.push({
            time: eb.time as Time,
            position: t.direction === "long" ? "belowBar" : "aboveBar",
            color: t.direction === "long" ? C.up : C.down,
            shape: t.direction === "long" ? "arrowUp" : "arrowDown",
            text: styleShowSignals ? (t.direction === "long" ? "buy" : "sell") : undefined,
          });
        }
        const xb = t.exit_bar !== null ? bars[t.exit_bar] : undefined;
        if (xb) {
          markers.push({
            time: xb.time as Time,
            position: t.direction === "long" ? "aboveBar" : "belowBar",
            color: t.pnl >= 0 ? "#2dd4a7" : "#b0596b",
            shape: "circle",
            text: styleShowQty ? `${t.pnl >= 0 ? "+" : ""}${t.pnl.toFixed(0)}` : undefined,
          });
        }
      }

      // Strategy trades broadcast from the Tester, keyed on timestamps
      // rather than bar indices so the chart does not need the same bar
      // list the backtest used.
      for (const st of strategyTrades) {
        const et = st.entry_ts ? Math.floor(new Date(st.entry_ts).getTime() / 1000) : null;
        const xt = st.exit_ts ? Math.floor(new Date(st.exit_ts).getTime() / 1000) : null;
        const eb = et !== null ? bars.find((b) => Math.abs(b.time - et) < 1) : undefined;
        const xb = xt !== null ? bars.find((b) => Math.abs(b.time - xt) < 1) : undefined;
        if (eb) {
          markers.push({
            time: eb.time as Time,
            position: st.direction === "long" ? "belowBar" : "aboveBar",
            color: st.direction === "long" ? C.up : C.down,
            shape: st.direction === "long" ? "arrowUp" : "arrowDown",
            text: styleShowSignals
              ? `#${st.number} ${st.direction === "long" ? "buy" : "sell"}`
              : undefined,
          });
        }
        if (xb) {
          markers.push({
            time: xb.time as Time,
            position: st.direction === "long" ? "aboveBar" : "belowBar",
            color: st.pnl >= 0 ? "#2dd4a7" : "#b0596b",
            shape: "circle",
            text: styleShowQty ? `${st.pnl >= 0 ? "+" : ""}${st.pnl.toFixed(0)}` : undefined,
          });
        }
      }
    }
    for (const lb of labels) {
      const bar = bars[lb.bar];
      if (!bar) continue;
      // yloc decides the side; `price` falls back to comparing against the
      // close, because a marker can only sit above or below a bar.
      const below = lb.yloc === "belowbar"
        ? true
        : lb.yloc === "abovebar"
          ? false
          : (lb.y ?? bar.close) < bar.close;
      markers.push({
        time: bar.time as Time,
        position: below ? "belowBar" : "aboveBar",
        color: lb.color ?? C.up,
        shape: below ? "arrowUp" : "arrowDown",
        // A label's text is often multi-line; a marker gets one line.
        text: lb.text.split("\n").join(" "),
      });
    }
    markers.sort((a, b) => Number(a.time) - Number(b.time));
    // Defer setMarkers to the next animation frame so a burst of
    // bars/shapes/trades updates during a live tick coalesces into one
    // marker-plugin repaint instead of several per frame.
    const raf = requestAnimationFrame(() => {
      markersApi.current?.setMarkers(markers);
    });
    return () => cancelAnimationFrame(raf);
  }, [shapes, trades, labels, bars, strategyTrades]);

  // ── horizontal lines on the price pane ───────────────────────────────
  useEffect(() => {
    const series = candles.current;
    if (!series) return;
    for (const line of overlayLines.current) series.removePriceLine(line);
    overlayLines.current = priceLines
      .filter((h) => h.price !== null && Number.isFinite(h.price))
      .map((h) =>
        series.createPriceLine({
          price: h.price as number,
          color: h.color,
          lineWidth: 1,
          lineStyle: h.style === "dotted" ? LineStyle.Dotted : LineStyle.Dashed,
          axisLabelVisible: true,
          title: h.title,
        }),
      );
  }, [priceLines, bars]);

  // ── native sub-pane series on the main chart ─────────────────────────
  //
  // Each PaneSpec in `panes` becomes a native sub-pane: `addSeries(def, opts,
  // paneIndex)` creates (or reuses) pane N and binds its series there. The
  // chart's single time scale and single crosshair govern every pane, so
  // alignment is free -- no cross-chart logical-range or crosshair sync.
  //
  // On each run we remove every series we've registered and re-create them,
  // which lets a pane's plot definition change (colour, line width, per-bar
  // colours) without us having to diff. preserveEmptyPane keeps the pane
  // alive during the brief interval between removeSeries and addSeries so
  // its paneIndex stays stable; after rebuild we trim any trailing panes
  // that no longer have a PaneSpec.
  useEffect(() => {
    const pc = priceChart.current;
    if (!pc) return;

    // Keep every existing pane alive while we tear down and re-add series,
    // so an index-N pane that momentarily empties doesn't disappear and
    // shift pane N+1 upwards.
    for (const p of pc.panes()) {
      try { p.setPreserveEmptyPane(true); } catch { /* ignore */ }
    }

    // Tear down old series + horizontal levels.
    for (const [, list] of paneSeries.current) {
      for (const s of list) {
        try { pc.removeSeries(s as ISeriesApi<"Line">); } catch { /* ignore */ }
      }
    }
    paneSeries.current.clear();
    panePriceLines.current.clear();

    // Re-create series for each PaneSpec, in order. paneIndex 0 is the
    // main (candles + volume) pane, so sub-panes start at 1.
    for (let i = 0; i < panes.length; i++) {
      const pane = panes[i]!;
      const paneIndex = i + 1;
      const list: ISeriesApi<"Line" | "Histogram">[] = [];
      for (const spec of pane.plots) {
        if (spec.kind === "histogram" || spec.kind === "columns") {
          const s = pc.addSeries(HistogramSeries, {
            color: spec.color,
            priceLineVisible: false,
            lastValueVisible: false,
          }, paneIndex);
          const explicit = spec.colors;
          s.setData(
            toLineData(spec.values, bars).map((d, j) => {
              // Whitespace entries (no `value`) are passed through unchanged
              // so warmup bars don't break the histogram render.
              if (!("value" in d)) return d;
              return {
                ...d,
                // Explicit per-bar colours win; otherwise a signed histogram
                // reads far better split by sign.
                color: explicit?.[j] ?? (d.value >= 0 ? spec.color : C.down),
              };
            }),
          );
          list.push(s);
        } else {
          const s = pc.addSeries(LineSeries, {
            color: spec.color,
            lineWidth: lineWidthOf(spec.linewidth),
            priceLineVisible: false,
            lastValueVisible: true,
          }, paneIndex);
          // A Pine plot may colour each bar -- a supertrend is green while
          // price is above it and red below. Only histograms were reading
          // that array, so a two-coloured line drew in one colour.
          const perBar = spec.colors;
          s.setData(
            perBar
              ? toLineData(spec.values, bars).map((d, j) => ({
                  ...d,
                  color: perBar[j] ?? spec.color,
                }))
              : toLineData(spec.values, bars),
          );
          list.push(s);
        }
      }
      paneSeries.current.set(pane.id, list);

      // Levels and bounded scale hang off the first series so they share
      // its price scale.
      const anchor = list[0];
      if (anchor) {
        const lines: IPriceLine[] = [];
        for (const lvl of pane.levels ?? []) {
          lines.push(
            anchor.createPriceLine({
              price: lvl.value,
              color: lvl.color,
              lineWidth: 1,
              lineStyle: lvl.style === "solid" ? LineStyle.Solid
                : lvl.style === "dotted" ? LineStyle.Dotted : LineStyle.Dashed,
              axisLabelVisible: true,
              title: lvl.label ?? "",
            }),
          );
        }
        panePriceLines.current.set(pane.id, lines);
        if (pane.scale && pane.scale.length === 2) {
          // A bounded oscillator should not rescale as data scrolls past.
          anchor.applyOptions({ autoscaleInfoProvider: () => ({
            priceRange: { minValue: pane.scale![0]!, maxValue: pane.scale![1]! },
          }) });
        }
      }
    }

    // Trim trailing panes left behind by removed PaneSpecs. removePane
    // shifts indices, so remove from the end.
    const want = panes.length + 1; // 1 main + N sub-panes
    for (let i = pc.panes().length - 1; i >= want; i--) {
      try { pc.removePane(i); } catch { /* ignore */ }
    }

    // Any remaining panes beyond index 0 should auto-remove when future
    // rebuilds empty them -- preserveEmptyPane was set only to keep indices
    // stable mid-rebuild.
    const finalPanes = pc.panes();
    for (let i = 1; i < finalPanes.length; i++) {
      try { finalPanes[i]!.setPreserveEmptyPane(false); } catch { /* ignore */ }
      // Apply stored height for the pane that just gained series.
      const spec = panes[i - 1];
      if (spec) {
        try { finalPanes[i]!.setHeight(paneHeightOfRef.current(spec.id)); } catch { /* ignore */ }
      }
    }

    // Trigger overlay layout recompute now that panes have settled.
    bumpLayout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panes, bars]);

  // ── apply persisted pane heights to native panes ──────────────────────
  //
  // The drag handle and the double-click reset write into `paneHeights`;
  // this effect pushes those into `IPaneApi.setHeight` on the matching
  // native sub-pane. Separated from the series-rebuild effect so a drag
  // doesn't force a full series rebuild on every mousemove.
  useEffect(() => {
    const pc = priceChart.current;
    if (!pc) return;
    const chartPanes = pc.panes();
    for (let i = 0; i < panes.length; i++) {
      const subPane = chartPanes[i + 1];
      if (!subPane) continue;
      try { subPane.setHeight(paneHeightOf(panes[i]!.id)); } catch { /* ignore */ }
    }
    bumpLayout();
    // paneHeightOf closes over `paneHeights`, which is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paneHeights, panes, ready]);

  // ── measure native panes for the HTML overlay layer ───────────────────
  //
  // Each sub-pane's title, legend, remove button and resize handle are HTML
  // overlays positioned absolutely over the chart canvas. The library draws
  // no pane header, so the overlays' Y offsets come from
  // `IPaneApi.getHTMLElement().getBoundingClientRect()` — more accurate than
  // accumulating `getHeight()` sums, because it accounts for the pane
  // separators and the time-axis strip the library draws under the panes.
  // Deferred to the next animation frame so the measurement happens AFTER
  // the library's own reflow in response to the change that triggered us.
  useEffect(() => {
    const pc = priceChart.current;
    const container = priceRef.current;
    if (!pc || !container) return;
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const containerRect = container.getBoundingClientRect();
      const next = pc.panes().map((pane) => {
        const el = pane.getHTMLElement();
        if (!el) return { top: 0, height: pane.getHeight() };
        const rect = el.getBoundingClientRect();
        return { top: rect.top - containerRect.top, height: rect.height };
      });
      setPaneLayout((prev) => {
        if (prev.length !== next.length) return next;
        for (let i = 0; i < next.length; i++) {
          const a = prev[i]!;
          const b = next[i]!;
          if (Math.abs(a.top - b.top) > 0.5 || Math.abs(a.height - b.height) > 0.5) {
            return next;
          }
        }
        return prev;
      });
    };
    const raf = requestAnimationFrame(measure);
    return () => { cancelled = true; cancelAnimationFrame(raf); };
  }, [layoutTick, ready]);

  // ── time axis in market time, at the timeframe's granularity ─────────
  //
  // lightweight-charts formats a UNIX timestamp in UTC, so the axis read four
  // hours ahead of the clock in the footer beside it -- a bar at 19:55 in New
  // York was labelled 23:55. Both now say the same thing, because the market
  // is the only clock these bars mean anything in.
  useEffect(() => {
    const secs = TF_SECONDS[timeframe] ?? 86400;
    const intraday = secs < 86400;
    // Variable-assigned object literal: `tickMarkFormatter` sits on
    // TimeScaleOptions, but ITimeScaleApi.applyOptions is typed on the
    // parent HorzScaleOptions. The literal-level excess property check
    // only fires on inline object arguments, not on a value previously
    // bound to a variable.
    const opts = {
      timeVisible: intraday,
      secondsVisible: secs < 60,
      tickMarkFormatter: (t: Time) => formatET(Number(t), secs, true),
    };
    priceChart.current?.timeScale().applyOptions(opts);
    const loc = { timeFormatter: (t: Time) => formatET(Number(t), secs, false) };
    priceChart.current?.applyOptions({ localization: loc });
    // Native sub-panes share the main chart's single time scale, so no
    // per-pane re-application is needed.
  }, [timeframe, ready]);

  // ── crosshair readout, shared across panes ───────────────────────────
  //
  // `suppressEmit` guards the external callback against the echo that would
  // otherwise happen when a sibling tile moves this chart's crosshair: the
  // move fires the subscription just as a user-driven one would, and emitting
  // it back out would ricochet between every tile.
  const suppressEmit = useRef(false);
  useEffect(() => {
    const pc = priceChart.current;
    if (!pc) return;
    const byTime = new Map<number, number>();
    bars.forEach((b, i) => byTime.set(b.time, i));
    // Crosshair handler fires on every mousemove frame. React setHover
    // triggers legend re-renders down the tree; batching the hover update
    // through rAF collapses several moves per frame into one paint and
    // kills the "hover feels sticky" sensation on dense charts.
    let hoverRaf: number | null = null;
    let pendingTime: number | null = null;
    let pendingClear = false;
    const flushHover = () => {
      hoverRaf = null;
      if (pendingClear) {
        setHover((prev) => (prev.bar === null ? prev : { bar: null, index: null }));
        if (!suppressEmit.current) onCrosshair?.(null);
        pendingClear = false;
        pendingTime = null;
        return;
      }
      if (pendingTime === null) return;
      const t = pendingTime;
      pendingTime = null;
      const idx = byTime.get(t);
      if (idx === undefined) return;
      const bar = bars[idx] ?? null;
      setHover((prev) => (prev.index === idx ? prev : { bar, index: idx }));
      if (!suppressEmit.current) onCrosshair?.(t);
    };
    const handler = (param: { time?: Time }) => {
      if (param.time === undefined) {
        pendingClear = true;
        pendingTime = null;
      } else {
        pendingTime = Number(param.time);
        pendingClear = false;
      }
      if (hoverRaf === null) hoverRaf = requestAnimationFrame(flushHover);
    };
    pc.subscribeCrosshairMove(handler);
    return () => {
      if (hoverRaf !== null) cancelAnimationFrame(hoverRaf);
      pc.unsubscribeCrosshairMove(handler);
    };
  }, [bars, onCrosshair]);

  // The vertical crosshair line is a native property of the chart in v5 --
  // one chart, one time scale, one crosshair that spans every pane. No
  // per-pane subscription or ricochet guard is needed.

  // ── crosshair set by a sibling tile ──────────────────────────────────
  //
  // The sibling's hover-time is translated to this chart's own
  // (price, time, series) via a close-price lookup at that bar. If the time
  // is not in this tile's bars (different symbol, different timeframe,
  // mismatched extended hours), the crosshair is cleared instead.
  useEffect(() => {
    const pc = priceChart.current;
    const series = candles.current;
    if (!pc || !series) return;
    suppressEmit.current = true;
    try {
      if (syncCrosshairTime == null) {
        pc.clearCrosshairPosition();
        return;
      }
      const bar = bars.find((b) => b.time === syncCrosshairTime);
      if (!bar) {
        pc.clearCrosshairPosition();
        return;
      }
      pc.setCrosshairPosition(bar.close, bar.time as Time, series);
    } finally {
      // The subscribe handler runs synchronously inside setCrosshairPosition,
      // so clearing the flag after the call is enough.
      suppressEmit.current = false;
    }
  }, [syncCrosshairTime, bars]);

  // ── strategy trades listener (Strategy Tester → chart) ─────────────
  //
  // The Strategy Tester publishes the latest backtest's trade list; the
  // chart draws entry/exit arrows for each one. Honours the Style tab's
  // "Trades on chart" toggle via localStorage so the tester can clear
  // the channel to hide them. See panels/BacktestPanel.tsx.
  useEffect(() => {
    const ch = new BroadcastChannel("tt-backtest-trades");
    const handler = (ev: MessageEvent<{ symbol?: string; trades: typeof strategyTrades }>) => {
      setStrategyTrades(ev.data?.trades ?? []);
    };
    ch.addEventListener("message", handler);
    return () => { ch.removeEventListener("message", handler); ch.close(); };
  }, []);

  // ── trade-focus listener (Strategy Tester → chart) ──────────────────
  //
  // When a user clicks a per-trade bar in the Strategy Tester, scroll this
  // chart to the trade's bar. Uses the same BroadcastChannel pattern as the
  // crosshair sync so it works from the main window AND pop-out windows.
  useEffect(() => {
    const ch = new BroadcastChannel("tt-trade-focus");
    const handler = (ev: MessageEvent<{ time: number }>) => {
      const t = ev.data?.time;
      const pc = priceChart.current;
      if (!pc || typeof t !== "number") return;
      const idx = bars.findIndex((b) => Math.abs(b.time - t) < 1);
      if (idx < 0) return;
      const span = 40;
      const from = Math.max(0, idx - Math.round(span / 2));
      const to = Math.min(bars.length - 1, from + span);
      pc.timeScale().setVisibleLogicalRange({ from, to });
      // Set the crosshair on the trade's exit bar so the user's eye catches it.
      const bar = bars[idx];
      const series = candles.current;
      if (bar && series) {
        try { pc.setCrosshairPosition(bar.close, bar.time as Time, series); }
        catch { /* v4 can throw on an uninitialised series; harmless. */ }
      }
    };
    ch.addEventListener("message", handler);
    return () => { ch.removeEventListener("message", handler); ch.close(); };
  }, [bars]);

  void ready; // re-render once the chart handles exist

  // The hovered bar, or the newest one when nothing is hovered.
  const readoutIndex = hover.index ?? (bars.length ? bars.length - 1 : null);

  const startPaneDrag = (id: string, ev: React.PointerEvent) => {
    ev.preventDefault();
    const startY = ev.clientY;
    const startH = paneHeightOf(id);
    const onMove = (e: PointerEvent) => {
      // Dragging up grows the pane below the handle; dragging down shrinks it.
      const dy = startY - e.clientY;
      const next = Math.max(PANE_MIN, Math.min(PANE_MAX, startH + dy));
      setPaneHeights((prev) => {
        const merged = { ...prev, [id]: next };
        savePaneHeights(merged);
        return merged;
      });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  const resetPaneHeight = (id: string) => {
    // Forget the stored height for this pane so it returns to the default.
    // Faster than dragging back to a nice value.
    setPaneHeights((prev) => {
      const next = { ...prev };
      delete next[id];
      savePaneHeights(next);
      return next;
    });
  };

  const eventStrip = renderEventStrip?.({ chart: priceChart.current });

  return (
    <div style={{
      display: "grid",
      gridTemplateRows: eventStrip ? "1fr auto auto" : "1fr auto",
      height: "100%", minHeight: 0,
    }}>
      <div style={{ position: "relative", minHeight: 0 }}>
        <div ref={priceRef} style={{ position: "absolute", inset: 0 }} />
        {renderPriceOverlay?.({ chart: priceChart.current, series: candles.current })}
        {tables.map((t) => <PineTablePanel key={t.id} table={t} />)}
        <div className="chart-overlay">
          {/* Always rendered, falling back to the newest bar.
           *
           * Mounting it only while hovering set up an oscillation: the legends
           * sit below it in the same flex column, so its appearance shoved
           * them down and its removal pulled them back up. The legend rows
           * take pointer events, so a cursor resting on one would leave the
           * canvas, clear the hover, collapse this row, move the legend out
           * from under the cursor, restore the hover -- several times a
           * second. Keeping the row mounted removes the feedback entirely,
           * and showing the last bar when nothing is hovered is what a chart
           * is expected to do anyway. */}
          {readoutIndex !== null && bars[readoutIndex] && (
            <BarReadout
              bar={bars[readoutIndex]!}
              previous={bars[readoutIndex - 1]}
              relativeVolume={relativeVolume[readoutIndex]}
            />
          )}
          {overlayGroups.map((g) => (
            <IndicatorLegend
              key={g.id}
              title={g.title}
              controls={g.controls}
              series={g.series.map((spec) => ({
                key: spec.key,
                name: spec.name,
                color: spec.color,
                value: valueAt(spec.values, hover.index),
              }))}
            />
          ))}
        </div>

        {/* Native sub-pane overlays: resize handle, title/legend, remove X.
         *
         * The library draws the pane itself (background, series, border); we
         * draw everything a reader interacts with. paneLayout[i + 1] is the
         * sub-pane's measured rect in the chart-container's coordinate
         * system (paneLayout[0] is the main pane). */}
        {panes.map((pane, i) => {
          const layout = paneLayout[i + 1];
          if (!layout) return null;
          return (
            <Fragment key={pane.id}>
              {/* Resize handle, centred on the pane's top border. */}
              <div
                className="chart-pane-resize"
                style={{
                  position: "absolute",
                  left: 0, right: 0,
                  top: layout.top - 3,
                  height: 6,
                  zIndex: 7,
                }}
                onPointerDown={(e) => startPaneDrag(pane.id, e)}
                onDoubleClick={() => resetPaneHeight(pane.id)}
                title="Drag to resize · double-click to reset"
              />
              {/* Title + legend overlay. */}
              <div
                className="chart-overlay"
                style={{ top: layout.top + 4, left: 10 }}
              >
                <IndicatorLegend
                  title={pane.title}
                  controls={pane.controls}
                  series={pane.plots.map((spec) => ({
                    key: spec.key,
                    name: spec.name,
                    color: spec.color,
                    value: valueAt(spec.values, hover.index),
                  }))}
                />
              </div>
              {/* The × remove button is only drawn for panes without their
               *  own controls block (built-ins and Pine panes get remove via
               *  the IndicatorLegend's menu). The hover-reveal rule in
               *  styles.css (`div:hover > .pane-remove`) still fires because
               *  this button is a direct child of the chart container. */}
              {!pane.controls && onRemovePane && (
                <button
                  className="pane-remove"
                  style={{ top: layout.top + 3 }}
                  title={`Remove ${pane.title}`}
                  onClick={() => onRemovePane(pane.id)}
                >
                  ×
                </button>
              )}
            </Fragment>
          );
        })}
      </div>

      {eventStrip}

      <ChartFooter
        chart={priceChart.current}
        caption={caption}
        live={live}
        onToggleLive={onToggleLive}
      />
    </div>
  );
}

/**
 * OHLC plus volume for the bar under the crosshair.
 *
 * Volume was absent here, which meant the only volume figure on screen was the
 * latest bar's in the header — so hovering a spike told you nothing about it.
 */
function BarReadout({
  bar, previous, relativeVolume,
}: {
  bar: Bar;
  previous?: Bar;
  relativeVolume?: number;
}) {
  const change = previous && previous.close !== 0
    ? ((bar.close - previous.close) / previous.close) * 100
    : null;
  const up = bar.close >= bar.open;
  const rel = relativeVolume;
  const heavy = rel !== undefined && Number.isFinite(rel) && rel >= 1.5;
  return (
    <div className="legend-row">
      <span style={{ color: "var(--text)" }}>
        O {bar.open.toFixed(2)} H {bar.high.toFixed(2)}{" "}
        L {bar.low.toFixed(2)} C{" "}
        <span className={up ? "up" : "down"}>{bar.close.toFixed(2)}</span>
      </span>
      {change !== null && (
        <span className={change >= 0 ? "up" : "down"}>
          {change >= 0 ? "+" : ""}{change.toFixed(2)}%
        </span>
      )}
      <span style={{ color: heavy ? "var(--tone-warn)" : "var(--text-dim)" }}>
        V {formatVolume(bar.volume)}
        {rel !== undefined && Number.isFinite(rel) && (
          <span className="faint"> · {rel.toFixed(1)}× avg</span>
        )}
      </span>
    </div>
  );
}

function valueAt(values: (number | null)[], index: number | null): string {
  if (index !== null) {
    const v = values[index];
    if (v !== null && v !== undefined) return v.toFixed(2);
    return "—";
  }
  for (let i = values.length - 1; i >= 0; i--) {
    const v = values[i];
    if (v !== null && v !== undefined) return v.toFixed(2);
  }
  return "—";
}

/** A script's table.new() panel, pinned to the corner it asked for.
 *
 * Rendered as a real grid rather than drawn on the canvas, so the text stays
 * selectable and scales with the page's font size. `position` carries both
 * axes -- "bottom_right" and so on -- which map straight onto the inset.
 */
function PineTablePanel({ table }: { table: PineTable }) {
  const [vert, horz] = table.position.split("_");
  const style: React.CSSProperties = {
    position: "absolute",
    zIndex: 3,
    pointerEvents: "none",
    display: "grid",
    gridTemplateColumns: `repeat(${table.columns}, auto)`,
    background: table.bgcolor ?? "rgba(12,16,24,0.86)",
    border: table.frame_width
      ? `${table.frame_width}px solid ${table.frame_color ?? "#2a3444"}`
      : "1px solid #2a3444",
    borderRadius: 6,
    overflow: "hidden",
    // Clear of the price scale on the right and the legend at the top left.
    ...(vert === "top" ? { top: 8 } : vert === "bottom" ? { bottom: 30 } : { top: "42%" }),
    ...(horz === "right" ? { right: 68 } : horz === "left" ? { left: 8 }
      : { left: "50%", transform: "translateX(-50%)" }),
  };
  const cells = [];
  for (let r = 0; r < table.rows; r++) {
    for (let c = 0; c < table.columns; c++) {
      const cell = table.cells[`${c},${r}`];
      // A row a script never filled should not leave a gap in the grid.
      if (!cell && !Object.keys(table.cells).some((k) => k.endsWith(`,${r}`))) continue;
      cells.push(
        <div
          key={`${c},${r}`}
          style={{
            padding: "3px 9px",
            fontSize: 12,
            lineHeight: 1.45,
            whiteSpace: "nowrap",
            textAlign: (cell?.halign as React.CSSProperties["textAlign"]) ?? "left",
            color: cell?.text_color ?? "#d6dde8",
            background: cell?.bgcolor ?? "transparent",
            borderTop: table.border_width && r > 0
              ? `${table.border_width}px solid ${table.border_color ?? "#222a36"}`
              : undefined,
          }}
          title={cell?.tooltip ?? undefined}
        >
          {cell?.text ?? ""}
        </div>,
      );
    }
  }
  return <div style={style}>{cells}</div>;
}

